// Real estate investment calculation formulas
// Adapted from Fairmeadow Capital

/**
 * Calculate monthly mortgage payment (P&I only)
 */
export function calculateMonthlyPayment(
  principal: number,
  annualRate: number,
  termYears: number
): number {
  if (principal <= 0 || termYears <= 0) return 0
  if (annualRate === 0) return principal / (termYears * 12)

  const monthlyRate = annualRate / 100 / 12
  const numPayments = termYears * 12
  const payment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
    (Math.pow(1 + monthlyRate, numPayments) - 1)

  return payment
}

/**
 * Calculate total interest paid over life of loan
 */
export function calculateTotalInterest(
  principal: number,
  monthlyPayment: number,
  termYears: number
): number {
  const totalPaid = monthlyPayment * termYears * 12
  return totalPaid - principal
}

/**
 * Calculate DSCR (Debt Service Coverage Ratio)
 */
export function calculateDSCR(
  monthlyIncome: number,
  monthlyDebtService: number
): number {
  if (monthlyDebtService <= 0) return 0
  return monthlyIncome / monthlyDebtService
}

/**
 * Calculate Cap Rate
 */
export function calculateCapRate(noi: number, purchasePrice: number): number {
  if (purchasePrice <= 0) return 0
  return (noi / purchasePrice) * 100
}

/**
 * Calculate Cash-on-Cash Return
 */
export function calculateCashOnCash(
  annualCashFlow: number,
  totalCashInvested: number
): number {
  if (totalCashInvested <= 0) return 0
  return (annualCashFlow / totalCashInvested) * 100
}

/**
 * Calculate Gross Rent Multiplier
 */
export function calculateGRM(
  purchasePrice: number,
  annualRent: number
): number {
  if (annualRent <= 0) return 0
  return purchasePrice / annualRent
}

/**
 * Calculate Break-Even Rent (rent needed to cover all expenses)
 */
export function calculateBreakEvenRent(
  monthlyExpenses: number,
  vacancyRate: number
): number {
  if (vacancyRate >= 100) return Infinity
  return monthlyExpenses / (1 - vacancyRate / 100)
}

/**
 * Calculate monthly cash flow
 */
export function calculateMonthlyCashFlow(
  monthlyRent: number,
  vacancyRate: number,
  monthlyExpenses: number,
  monthlyDebtService: number
): number {
  const effectiveRent = monthlyRent * (1 - vacancyRate / 100)
  return effectiveRent - monthlyExpenses - monthlyDebtService
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Format percentage for display
 */
export function formatPercent(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Format number with commas
 */
export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}
